import React, { useState } from "react";
import { Activity, AlertTriangle, ShieldCheck, Users, RefreshCw, TrendingUp, CheckCircle2 } from "lucide-react";

const DATA = {
  Week: {
    metrics: [
      { icon: Activity, label: "AI systems monitored", value: "9" },
      { icon: Activity, label: "Interactions", value: "11,480" },
      { icon: AlertTriangle, label: "Policy triggers", value: "312" },
      { icon: Users, label: "Human escalations", value: "24" },
      { icon: RefreshCw, label: "Repeated incidents", value: "6" },
      { icon: CheckCircle2, label: "Resolved", value: "88%" },
    ],
    changed: ["Advice-boundary flags up 12% on customer chat", "One new recurring theme in claims calls", "Vulnerability-indicator reviews cleared within SLA"],
  },
  Month: {
    metrics: [
      { icon: Activity, label: "AI systems monitored", value: "9" },
      { icon: Activity, label: "Interactions", value: "48,210" },
      { icon: AlertTriangle, label: "Policy triggers", value: "1,190" },
      { icon: Users, label: "Human escalations", value: "97" },
      { icon: RefreshCw, label: "Repeated incidents", value: "18" },
      { icon: CheckCircle2, label: "Resolved", value: "91%" },
    ],
    changed: ["Sensitive-information blocks trending down after policy update", "Employee-AI flags concentrated in two teams", "Customer-outcome indicators stable month-on-month"],
  },
  Quarter: {
    metrics: [
      { icon: Activity, label: "AI systems monitored", value: "11" },
      { icon: Activity, label: "Interactions", value: "146,900" },
      { icon: AlertTriangle, label: "Policy triggers", value: "3,540" },
      { icon: Users, label: "Human escalations", value: "281" },
      { icon: RefreshCw, label: "Repeated incidents", value: "44" },
      { icon: CheckCircle2, label: "Resolved", value: "93%" },
    ],
    changed: ["New AI systems onboarded to monitoring", "Advice-boundary policy triggers most frequently", "Mortgage-call journeys flagged for closer attention"],
  },
};

const RANGES = ["Week", "Month", "Quarter"];

export default function GovernanceDashboard() {
  const [range, setRange] = useState("Month");
  const cur = DATA[range];

  return (
    <div className="rounded-lg border border-white/10 bg-navy-900/70 p-5 sm:p-7" data-testid="fs-governance-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 pb-4">
        <div>
          <p className="text-sm font-semibold text-foreground">Governance & Outcomes Dashboard</p>
          <p className="text-xs text-muted-foreground/70">Synthetic demonstration data</p>
        </div>
        <div className="inline-flex rounded-lg border border-white/12 bg-navy-800/60 p-0.5">
          {RANGES.map((r) => (
            <button key={r} onClick={() => setRange(r)} data-testid={`fs-dash-${r.toLowerCase()}`}
              className={`rounded-md px-3.5 py-1.5 text-xs font-medium transition-colors ${range === r ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{r}</button>
          ))}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
        {cur.metrics.map((m) => {
          const MIcon = m.icon;
          return (
            <div key={m.label} className="rounded-xl border border-white/10 bg-navy-800/50 p-4">
              <MIcon className="h-4 w-4 text-primary" />
              <p className="mt-2.5 text-2xl font-bold text-foreground">{m.value}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{m.label}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-5 rounded-xl border border-white/10 bg-navy-800/50 p-5">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary" />
          <p className="text-sm font-semibold text-foreground">What changed?</p>
        </div>
        <ul className="mt-3 space-y-2">
          {cur.changed.map((c) => (
            <li key={c} className="flex items-start gap-2.5 text-sm text-muted-foreground"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70" /> {c}</li>
          ))}
        </ul>
        <div className="mt-4 grid sm:grid-cols-2 gap-2 text-xs text-muted-foreground/80">
          {["Where are risks increasing?", "Which policies trigger most?", "Which customer journeys need attention?", "What actions have reviewers taken?"].map((q) => (
            <div key={q} className="rounded-lg border border-white/10 bg-navy-900/60 px-3 py-2">{q}</div>
          ))}
        </div>
      </div>
      <p className="mt-4 text-xs text-muted-foreground/70 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Shows how operational interaction data could support governance and oversight. The firm determines its own obligations.</p>
    </div>
  );
}
